import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { PageHeader, Card, Tabs } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { KeyRound, Cpu, Send, Mail, Smartphone, Cloud, CheckCircle2, AlertTriangle, Save, Inbox, CreditCard, MessageSquare, Wifi, RefreshCw, ExternalLink, Copy, Sparkles, Building2, Users, Trash2 } from 'lucide-react';

const Badge = ({ ok, labelOk = 'Konfigurert', labelNo = 'Ikke konfigurert' }) =>
  ok ? (
    <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <CheckCircle2 size={11} /> {labelOk}
    </span>
  ) : (
    <span className="badge badge-warning" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <AlertTriangle size={11} /> {labelNo}
    </span>
  );

const Admin = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [firma, setFirma] = useState(null);
  const [feil, setFeil] = useState('');
  const [busy, setBusy] = useState(false);
  const [lagretMld, setLagretMld] = useState('');
  const [seksjonMld, setSeksjonMld] = useState({});
  const [fane, setFane] = useState('org');

  const last = async () => {
    try {
      const [s, f] = await Promise.all([api.get('/admin/status'), api.get('/firma')]);
      setStatus(s.data);
      setFirma(f.data);
    } catch (e) {
      setFeil(e.response?.data?.error || 'Kunne ikke laste admin-status.');
    }
  };
  useEffect(() => { last(); }, []);

  const lagreOpenAI = async () => {
    setBusy(true);
    setLagretMld('');
    try {
      await api.put('/firma', {
        openai_api_key: firma.openai_api_key || '',
        openai_modell: firma.openai_modell || 'gpt-4o-mini',
      });
      setLagretMld('Lagret.');
      await last();
    } catch (e) {
      setLagretMld(e.response?.data?.error || 'Lagring feilet.');
    }
    setBusy(false);
  };

  const lagreFelter = async (felter, mldKey) => {
    setBusy(true);
    setSeksjonMld({ ...seksjonMld, [mldKey]: '' });
    try {
      const payload = {};
      felter.forEach((k) => { if (firma[k] !== undefined) payload[k] = firma[k]; });
      await api.put('/firma', payload);
      // tøm lokale verdier på sensitive felt etter lagring (vi skal ikke holde dem i state)
      const cleared = { ...firma };
      ['altinn_jwk', 'peppol_ap_token', 'peppol_innkommende_secret',
       'vipps_client_secret', 'vipps_subscription_key'].forEach((k) => {
        if (felter.includes(k)) cleared[k] = '';
      });
      setFirma(cleared);
      setSeksjonMld((m) => ({ ...m, [mldKey]: 'Lagret.' }));
      await last();
    } catch (e) {
      setSeksjonMld((m) => ({ ...m, [mldKey]: e.response?.data?.error || 'Lagring feilet.' }));
    }
    setBusy(false);
  };

  if (!user?.er_superadmin) {
    return (
      <>
        <PageHeader title="Admin" subtitle="Plattform-administrator-tilgang kreves" />
        <Card>
          <p className="fm-text-muted" style={{ fontSize: 12, margin: 0 }}>
            Denne siden er kun synlig for plattform-administrator (Regnino-driver).
          </p>
        </Card>
      </>
    );
  }

  if (feil) return (
    <>
      <PageHeader title="Admin" />
      <Card><p style={{ color: 'var(--theme-danger, #c62828)', margin: 0 }}>{feil}</p></Card>
    </>
  );

  if (!status || !firma) return (
    <>
      <PageHeader title="Admin" />
      <Card><p className="fm-text-muted" style={{ margin: 0 }}>Laster…</p></Card>
    </>
  );

  return (
    <>
      <PageHeader
        title="Admin"
        subtitle="Eier-tilgang: API-nøkler, integrasjoner og konfigurasjon"
      />

      <Tabs value={fane} onChange={setFane} items={[
        { key: 'org',           label: 'Organisasjoner', icon: Building2 },
        { key: 'brukere',       label: 'Brukere',        icon: Users },
        { key: 'fakturering',   label: 'Fakturering (EHF)', icon: Inbox },
        { key: 'tjenester',     label: 'Tjenester',      icon: Sparkles },
        { key: 'integrasjoner', label: 'Integrasjoner & nøkler', icon: KeyRound },
      ]} />

      {fane === 'org' && <OrganisasjonerAdmin />}

      {fane === 'brukere' && <BrukereAdmin />}

      {fane === 'fakturering' && <AbonnementFakturering />}

      {fane === 'tjenester' && <TjenesterAdmin />}

      {fane === 'integrasjoner' && (<>
      <PlattformConfig />

      <BankForbindelserAdmin gcKonfigurert={status.gocardless?.konfigurert}
                             ebKonfigurert={status.enablebanking?.konfigurert} />

      {/* OpenAI / AI / OCR */}
      <Card>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Cpu size={14} /> AI / OCR (OpenAI)
          <span style={{ marginLeft: 'auto' }}>
            <Badge ok={status.openai.firma_key_satt || status.openai.miljo_key_satt} />
          </span>
        </h3>
        <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 12px' }}>
          Brukes til automatisk lesing av kvitteringer og bilag (Vision OCR).
          Hent en nøkkel på <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">platform.openai.com/api-keys</a>.
          La feltet stå tomt for å beholde eksisterende nøkkel. Skriv «-» og lagre for å fjerne.
        </p>
        <div className="grid" style={{ gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <div>
            <label>API-nøkkel {firma.openai_api_key_satt && <span className="badge badge-success" style={{ fontSize: 9 }}>satt</span>}</label>
            <input type="password" value={firma.openai_api_key || ''}
                   onChange={(e) => setFirma({ ...firma, openai_api_key: e.target.value })}
                   placeholder={firma.openai_api_key_satt ? '••••••••' : 'sk-...'} />
          </div>
          <div>
            <label>Modell</label>
            <input value={firma.openai_modell || 'gpt-4o-mini'}
                   onChange={(e) => setFirma({ ...firma, openai_modell: e.target.value })}
                   placeholder="gpt-4o-mini" />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <button className="btn-primary" disabled={busy} onClick={lagreOpenAI}>
            <Save size={14} /> Lagre AI-innstillinger
          </button>
          {lagretMld && <span className="fm-text-muted" style={{ fontSize: 12 }}>{lagretMld}</span>}
        </div>
        {status.openai.miljo_key_satt && (
          <p className="fm-text-muted" style={{ fontSize: 11, margin: '10px 0 0' }}>
            Merk: <code>OPENAI_API_KEY</code> er også satt som server-miljøvariabel og vil brukes som fallback.
          </p>
        )}
      </Card>

      {/* Altinn */}
      <Card style={{ marginTop: 12 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Send size={14} /> Altinn (Maskinporten OAuth2)
          <span style={{ marginLeft: 'auto' }}><Badge ok={status.altinn?.konfigurert} /></span>
        </h3>
        <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 10px' }}>
          Brukes for å sende MVA- og A-meldinger direkte til Skatteetaten. Krev tildelt
          Maskinporten-klient og en privat JWK. Tomt sensitivt felt = behold lagret verdi. Skriv «-» for å slette.
        </p>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label>Org.nr (avsender)</label>
            <input value={firma.altinn_org_nr || ''}
                   onChange={(e) => setFirma({ ...firma, altinn_org_nr: e.target.value })}
                   placeholder={firma.orgnr || '999999999'} />
          </div>
          <div>
            <label>Miljø</label>
            <select value={firma.altinn_miljo || 'tt02'}
                    onChange={(e) => setFirma({ ...firma, altinn_miljo: e.target.value })}>
              <option value="tt02">tt02 (test)</option>
              <option value="prod">prod (produksjon)</option>
            </select>
          </div>
          <div>
            <label>Maskinporten Client ID</label>
            <input value={firma.altinn_client_id || ''}
                   onChange={(e) => setFirma({ ...firma, altinn_client_id: e.target.value })}
                   placeholder="abcd1234-..." />
          </div>
          <div>
            <label>JWK Key ID (kid)</label>
            <input value={firma.altinn_kid || ''}
                   onChange={(e) => setFirma({ ...firma, altinn_kid: e.target.value })}
                   placeholder="my-key-1" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>
              Privat JWK (JSON) {firma.altinn_jwk_satt && <span className="badge badge-success" style={{ fontSize: 9 }}>satt</span>}
            </label>
            <textarea rows={4} value={firma.altinn_jwk || ''}
                      onChange={(e) => setFirma({ ...firma, altinn_jwk: e.target.value })}
                      placeholder={firma.altinn_jwk_satt ? '••••• (lagret) — la stå tom for å beholde' : '{"kty":"RSA","n":"...","d":"..."}'} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <button className="btn-primary" disabled={busy}
                  onClick={() => lagreFelter(['altinn_org_nr', 'altinn_miljo', 'altinn_client_id', 'altinn_kid', 'altinn_jwk'], 'altinn')}>
            <Save size={14} /> Lagre Altinn
          </button>
          {seksjonMld.altinn && <span className="fm-text-muted" style={{ fontSize: 12 }}>{seksjonMld.altinn}</span>}
        </div>
      </Card>

      {/* Peppol */}
      <Card style={{ marginTop: 12 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Inbox size={14} /> Peppol (Access Point)
          <span style={{ marginLeft: 'auto' }}><Badge ok={status.peppol?.konfigurert} /></span>
        </h3>
        <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 10px' }}>
          Sett URL og token for din Peppol-leverandør (AP). Sender-ID på formen
          <code> 0192:999999999</code>. <code>PEPPOL_INCOMING_SECRET</code> sikrer endepunktet
          <code> /api/peppol/inn</code> mot uautoriserte poster.
        </p>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>AP URL</label>
            <input value={firma.peppol_ap_url || ''}
                   onChange={(e) => setFirma({ ...firma, peppol_ap_url: e.target.value })}
                   placeholder="https://ap.example.com" />
          </div>
          <div>
            <label>Sender-ID</label>
            <input value={firma.peppol_sender_id || ''}
                   onChange={(e) => setFirma({ ...firma, peppol_sender_id: e.target.value })}
                   placeholder="0192:999999999" />
          </div>
          <div>
            <label>
              AP Token {firma.peppol_ap_token_satt && <span className="badge badge-success" style={{ fontSize: 9 }}>satt</span>}
            </label>
            <input type="password" value={firma.peppol_ap_token || ''}
                   onChange={(e) => setFirma({ ...firma, peppol_ap_token: e.target.value })}
                   placeholder={firma.peppol_ap_token_satt ? '••••••••' : 'Bearer-token'} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>
              Innkommende secret {firma.peppol_innkommende_secret_satt && <span className="badge badge-success" style={{ fontSize: 9 }}>satt</span>}
            </label>
            <input type="password" value={firma.peppol_innkommende_secret || ''}
                   onChange={(e) => setFirma({ ...firma, peppol_innkommende_secret: e.target.value })}
                   placeholder={firma.peppol_innkommende_secret_satt ? '••••••••' : 'tilfeldig streng'} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <button className="btn-primary" disabled={busy}
                  onClick={() => lagreFelter(['peppol_ap_url', 'peppol_sender_id', 'peppol_ap_token', 'peppol_innkommende_secret'], 'peppol')}>
            <Save size={14} /> Lagre Peppol
          </button>
          {seksjonMld.peppol && <span className="fm-text-muted" style={{ fontSize: 12 }}>{seksjonMld.peppol}</span>}
        </div>
      </Card>

      {/* SMTP */}
      <Card style={{ marginTop: 12 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Mail size={14} /> E-post (SMTP)
          <span style={{ marginLeft: 'auto' }}><Badge ok={status.smtp?.konfigurert || status.smtp?.plattform_aktiv} /></span>
        </h3>
        <p className="fm-text-muted" style={{ fontSize: 12, margin: 0 }}>
          {status.smtp?.konfigurert
            ? <>Firma-egen SMTP. Host: <b>{status.smtp.host}</b> · Avsender: <b>{status.smtp.avsender}</b>. Konfigureres på Innstillinger-siden.</>
            : status.smtp?.plattform_aktiv
              ? <>Plattform-SMTP aktiv (Regnino). Avsender: <b>{status.smtp.plattform_avsender}</b>. Firmaer kan overstyre med egen SMTP under Innstillinger.</>
              : <>SMTP er ikke konfigurert. Sett plattform-SMTP under Plattform-config, eller gå til <a href="/innstillinger">Innstillinger</a> for firma-egen.</>}
        </p>
      </Card>

      {/* Vipps */}
      <Card style={{ marginTop: 12 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Smartphone size={14} /> Vipps eCom
          <span style={{ marginLeft: 'auto' }}><Badge ok={status.vipps?.konfigurert} /></span>
        </h3>
        <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 10px' }}>
          Brukes for Vipps-betaling på fakturaer og abonnement. Hent verdier fra
          {' '}<a href="https://portal.vipps.no" target="_blank" rel="noreferrer">portal.vipps.no</a>.
        </p>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label>MSN (Merchant Serial Number)</label>
            <input value={firma.vipps_msn || ''}
                   onChange={(e) => setFirma({ ...firma, vipps_msn: e.target.value })}
                   placeholder="123456" />
          </div>
          <div>
            <label>Miljø</label>
            <select value={firma.vipps_miljo || 'test'}
                    onChange={(e) => setFirma({ ...firma, vipps_miljo: e.target.value })}>
              <option value="test">test (apitest.vipps.no)</option>
              <option value="prod">prod (api.vipps.no)</option>
            </select>
          </div>
          <div>
            <label>Client ID</label>
            <input value={firma.vipps_client_id || ''}
                   onChange={(e) => setFirma({ ...firma, vipps_client_id: e.target.value })}
                   placeholder="abc-..." />
          </div>
          <div>
            <label>
              Client Secret {firma.vipps_client_secret_satt && <span className="badge badge-success" style={{ fontSize: 9 }}>satt</span>}
            </label>
            <input type="password" value={firma.vipps_client_secret || ''}
                   onChange={(e) => setFirma({ ...firma, vipps_client_secret: e.target.value })}
                   placeholder={firma.vipps_client_secret_satt ? '••••••••' : ''} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>
              Subscription Key {firma.vipps_subscription_key_satt && <span className="badge badge-success" style={{ fontSize: 9 }}>satt</span>}
            </label>
            <input type="password" value={firma.vipps_subscription_key || ''}
                   onChange={(e) => setFirma({ ...firma, vipps_subscription_key: e.target.value })}
                   placeholder={firma.vipps_subscription_key_satt ? '••••••••' : 'Ocp-Apim-Subscription-Key'} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <button className="btn-primary" disabled={busy}
                  onClick={() => lagreFelter(['vipps_msn', 'vipps_miljo', 'vipps_client_id', 'vipps_client_secret', 'vipps_subscription_key'], 'vipps')}>
            <Save size={14} /> Lagre Vipps
          </button>
          {seksjonMld.vipps && <span className="fm-text-muted" style={{ fontSize: 12 }}>{seksjonMld.vipps}</span>}
        </div>
      </Card>

      {/* Stripe (abonnement) */}
      <Card style={{ marginTop: 12 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <CreditCard size={14} /> Stripe (abonnement)
          <span style={{ marginLeft: 'auto' }}><Badge ok={firma.stripe_secret_key_satt} /></span>
        </h3>
        <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 10px' }}>
          Aktiverer Stripe Checkout for abonnement (Starter / Pro / Business). Hent <b>Secret key</b> fra
          Stripe → Developers → API keys, og <b>Price IDs</b> (price_...) fra hvert produkt under Products.
          <b> Webhook secret</b> hentes etter at endpoint <code>/api/abonnement/webhook</code> er lagt til
          i Developers → Webhooks (events: checkout.session.completed, customer.subscription.*).
          Tom verdi = behold; <code>-</code> = slett.
        </p>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>
              Secret key {firma.stripe_secret_key_satt && <span className="badge badge-success" style={{ fontSize: 9 }}>satt</span>}
            </label>
            <input type="password" value={firma.stripe_secret_key || ''}
                   onChange={(e) => setFirma({ ...firma, stripe_secret_key: e.target.value })}
                   placeholder={firma.stripe_secret_key_satt ? '••••••••' : 'sk_live_... eller sk_test_...'} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>
              Webhook signing secret {firma.stripe_webhook_secret_satt && <span className="badge badge-success" style={{ fontSize: 9 }}>satt</span>}
            </label>
            <input type="password" value={firma.stripe_webhook_secret || ''}
                   onChange={(e) => setFirma({ ...firma, stripe_webhook_secret: e.target.value })}
                   placeholder={firma.stripe_webhook_secret_satt ? '••••••••' : 'whsec_...'} />
          </div>
          <div>
            <label>Price ID — Starter</label>
            <input value={firma.stripe_price_starter || ''}
                   onChange={(e) => setFirma({ ...firma, stripe_price_starter: e.target.value })}
                   placeholder="price_..." />
          </div>
          <div>
            <label>Price ID — Pro</label>
            <input value={firma.stripe_price_pro || ''}
                   onChange={(e) => setFirma({ ...firma, stripe_price_pro: e.target.value })}
                   placeholder="price_..." />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Price ID — Business</label>
            <input value={firma.stripe_price_business || ''}
                   onChange={(e) => setFirma({ ...firma, stripe_price_business: e.target.value })}
                   placeholder="price_..." />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <button className="btn-primary" disabled={busy}
                  onClick={() => lagreFelter(['stripe_secret_key', 'stripe_webhook_secret', 'stripe_price_starter', 'stripe_price_pro', 'stripe_price_business'], 'stripe')}>
            <Save size={14} /> Lagre Stripe
          </button>
          {seksjonMld.stripe && <span className="fm-text-muted" style={{ fontSize: 12 }}>{seksjonMld.stripe}</span>}
        </div>
      </Card>

      {/* S3 / R2 backup */}
      <Card style={{ marginTop: 12 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Cloud size={14} /> Daglig backup (S3 / Cloudflare R2 / Backblaze B2)
          <span style={{ marginLeft: 'auto' }}><Badge ok={status.s3_backup?.konfigurert} /></span>
        </h3>
        <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 10px' }}>
          Aktiverer automatisk daglig backup av database og vedlegg til S3-kompatibel lagring.
          For <b>Cloudflare R2</b>: endpoint = <code>https://&lt;account&gt;.r2.cloudflarestorage.com</code>,
          region = <code>auto</code>. La endpoint stå tom for AWS S3.
        </p>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label>Bucket</label>
            <input value={firma.s3_bucket || ''}
                   onChange={(e) => setFirma({ ...firma, s3_bucket: e.target.value })}
                   placeholder="regnskap-backup" />
          </div>
          <div>
            <label>Region</label>
            <input value={firma.s3_region || ''}
                   onChange={(e) => setFirma({ ...firma, s3_region: e.target.value })}
                   placeholder="auto (R2) eller eu-north-1" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Endpoint URL</label>
            <input value={firma.s3_endpoint || ''}
                   onChange={(e) => setFirma({ ...firma, s3_endpoint: e.target.value })}
                   placeholder="https://<account>.r2.cloudflarestorage.com (tom = AWS S3)" />
          </div>
          <div>
            <label>Access Key ID</label>
            <input value={firma.s3_access_key || ''}
                   onChange={(e) => setFirma({ ...firma, s3_access_key: e.target.value })}
                   placeholder="AKIA... eller R2-key" />
          </div>
          <div>
            <label>
              Secret Access Key {firma.s3_secret_key_satt && <span className="badge badge-success" style={{ fontSize: 9 }}>satt</span>}
            </label>
            <input type="password" value={firma.s3_secret_key || ''}
                   onChange={(e) => setFirma({ ...firma, s3_secret_key: e.target.value })}
                   placeholder={firma.s3_secret_key_satt ? '••••••••' : ''} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <button className="btn-primary" disabled={busy}
                  onClick={() => lagreFelter(['s3_bucket', 's3_endpoint', 's3_region', 's3_access_key', 's3_secret_key'], 's3')}>
            <Save size={14} /> Lagre backup-konfig
          </button>
          {seksjonMld.s3 && <span className="fm-text-muted" style={{ fontSize: 12 }}>{seksjonMld.s3}</span>}
        </div>
        {status.s3_backup?.konfigurert && (
          <p className="fm-text-muted" style={{ fontSize: 11, margin: '10px 0 0' }}>
            Aktiv: bucket <b>{status.s3_backup.bucket}</b> · endpoint <b>{status.s3_backup.endpoint}</b> · region <b>{status.s3_backup.region}</b>.
            Daglig kjøring starter automatisk; manuell kjøring under Innstillinger → Backup.
          </p>
        )}
      </Card>

      <Card style={{ marginTop: 12 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <KeyRound size={14} /> Eier-konto
        </h3>
        <p className="fm-text-muted" style={{ fontSize: 12, margin: 0 }}>
          Du er logget inn som <b>{user.epost}</b> og er registrert som eier av firmaet
          (første bruker opprettet for firma_id={user.firma_id}). Eier kan ikke overføres fra UI ennå.
        </p>
      </Card>
      </>)}
    </>
  );
};

const AbonnementFakturering = () => {
  const [rader, setRader] = useState([]);
  const [mld, setMld] = useState({});
  const [busy, setBusy] = useState('');

  const last = async () => {
    try {
      const r = await api.get('/admin/firma-liste');
      setRader(r.data || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { last(); }, []);

  const sendEhf = async (id) => {
    if (!window.confirm('Generer og send EHF abonnementsfaktura for dette firmaet?')) return;
    setBusy('send-' + id); setMld({ ...mld, [id]: '' });
    try {
      const r = await api.post(`/admin/abonnement/send-ehf/${id}`);
      setMld((m) => ({ ...m, [id]: `Sendt (${r.data.fakturanr || 'OK'})` }));
    } catch (e) {
      setMld((m) => ({ ...m, [id]: e.response?.data?.error || 'Feilet.' }));
    } finally { setBusy(''); }
  };

  const lastNed = (id) => {
    window.open(`/api/admin/abonnement/forhandsvis-ehf/${id}`, '_blank');
  };

  return (
    <Card style={{ marginTop: 12 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Inbox size={14} /> Abonnement-fakturering (EHF via Peppol)
      </h3>
      <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 10px' }}>
        Send månedlig EHF-faktura for abonnement til kundene dine via <b>Storecove</b> (Peppol Access Point).
        Konfigurer i Innstillinger → Integrasjoner → Peppol på eier-firmaet:
        <br />• <b>AP URL</b>: <code>https://api.storecove.com/api/v2</code>
        <br />• <b>AP Token</b>: API-nøkkel fra <a href="https://app.storecove.com/" target="_blank" rel="noreferrer">app.storecove.com</a>
        <br />• <b>Sender-ID</b>: numerisk <code>legal_entity_id</code> fra Storecove (sender du oppretter der)
        <br /><b>Auto-utsendelse:</b> firmaer som har valgt EHF som betalingsmetode under Innstillinger → Abonnement
        får faktura automatisk månedlig via cron-endepunktet
        <code> POST /api/abonnement/ehf/send-due</code> (X-Cron-Secret = CRON_SECRET).
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--theme-border)' }}>
              <th style={{ textAlign: 'left', padding: 6 }}>#</th>
              <th style={{ textAlign: 'left', padding: 6 }}>Firma</th>
              <th style={{ textAlign: 'left', padding: 6 }}>Orgnr</th>
              <th style={{ textAlign: 'left', padding: 6 }}>Plan</th>
              <th style={{ textAlign: 'left', padding: 6 }}>Metode</th>
              <th style={{ textAlign: 'left', padding: 6 }}>Sist EHF</th>
              <th style={{ textAlign: 'right', padding: 6 }}>Handling</th>
            </tr>
          </thead>
          <tbody>
            {rader.map((r) => (
              <tr key={r.id} style={{ borderBottom: '1px solid var(--theme-border-light, #2222)' }}>
                <td style={{ padding: 6 }}>{r.id}</td>
                <td style={{ padding: 6 }}>{r.navn}</td>
                <td style={{ padding: 6 }}>{r.orgnr || '—'}</td>
                <td style={{ padding: 6 }}>{r.plan}</td>
                <td style={{ padding: 6 }}>
                  <span className={r.betalingsmetode === 'ehf' ? 'badge badge-success' : 'badge'}
                        style={{ fontSize: 9 }}>
                    {r.betalingsmetode || 'manuell'}
                  </span>
                </td>
                <td style={{ padding: 6 }}>{r.ehf_sist_sendt || '—'}</td>
                <td style={{ padding: 6, textAlign: 'right' }}>
                  <button className="btn-ghost" style={{ fontSize: 10, padding: '3px 6px', marginRight: 4 }}
                          onClick={() => lastNed(r.id)} disabled={r.plan === 'free'}>
                    Forhåndsvis XML
                  </button>
                  <button className="btn-primary" style={{ fontSize: 10, padding: '3px 6px' }}
                          onClick={() => sendEhf(r.id)}
                          disabled={r.plan === 'free' || !r.orgnr || busy === 'send-' + r.id}>
                    Send EHF
                  </button>
                  {mld[r.id] && (
                    <div className="fm-text-muted" style={{ fontSize: 10, marginTop: 2 }}>{mld[r.id]}</div>
                  )}
                </td>
              </tr>
            ))}
            {!rader.length && (
              <tr><td colSpan="7" style={{ padding: 8, textAlign: 'center' }} className="fm-text-muted">Ingen firma.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const PlattformConfig = () => {
  const [konfig, setKonfig] = useState(null);
  const [verdier, setVerdier] = useState({});
  const [mld, setMld] = useState('');
  const [testTil, setTestTil] = useState('');
  const [testMld, setTestMld] = useState('');
  const [feil, setFeil] = useState('');

  const last = async () => {
    try {
      const r = await api.get('/admin/plattform-config');
      setKonfig(r.data.konfig || []);
      const init = {};
      (r.data.konfig || []).forEach((k) => { if (!k.sensitive) init[k.nokkel] = k.verdi; });
      setVerdier(init);
      setFeil('');
    } catch (e) {
      setFeil(e.response?.status === 403
        ? 'Kun plattform-superadmin har tilgang til denne seksjonen.'
        : ('Kunne ikke laste: ' + (e.response?.data?.error || e.message)));
      setKonfig([]);
    }
  };
  useEffect(() => { last(); }, []);

  const lagre = async () => {
    setMld('');
    try {
      await api.put('/admin/plattform-config', verdier);
      setMld('Lagret.');
      setVerdier((v) => {
        const ut = { ...v };
        (konfig || []).forEach((k) => { if (k.sensitive) ut[k.nokkel] = ''; });
        return ut;
      });
      await last();
    } catch (e) {
      setMld(e.response?.data?.error || 'Lagring feilet.');
    }
  };

  const testTwilio = async () => {
    setTestMld('Sender…');
    try {
      const r = await api.post('/admin/plattform-config/test-twilio', { til: testTil });
      setTestMld(r.data.ok ? '✓ Sendt' : 'Feil: ' + (r.data.feil || ''));
    } catch (e) {
      setTestMld('Feil: ' + (e.response?.data?.error || e.message));
    }
  };

  if (!konfig) return null;

  return (
    <Card style={{ marginBottom: 12 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
        <MessageSquare size={14} /> Plattform-integrasjoner (Twilio, SMS)
      </h3>
      <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 12px' }}>
        Sentrale plattform-secrets som <b>alle</b> firmaer på Regnino bruker. Bare du som
        plattform-superadmin kan sette disse. Brukerne ser kun «konfigurert»-status —
        aldri selve hemmelighetene. Tomt felt på passord/token = behold gammel verdi.
        Skriv «-» for å slette.
      </p>
      {feil && (
        <p style={{ fontSize: 12, color: 'var(--theme-danger, #ef4444)', margin: 0 }}>{feil}</p>
      )}
      {!feil && (<>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {konfig.map((k) => (
          <div key={k.nokkel}>
            <label>
              {k.label}{' '}
              {k.db_satt
                ? <span className="badge badge-success" style={{ fontSize: 9 }}>DB</span>
                : k.env_satt
                  ? <span className="badge" style={{ fontSize: 9 }}>ENV</span>
                  : <span className="badge badge-warning" style={{ fontSize: 9 }}>mangler</span>}
            </label>
            <input
              type={k.sensitive ? 'password' : 'text'}
              value={verdier[k.nokkel] || ''}
              onChange={(e) => setVerdier({ ...verdier, [k.nokkel]: e.target.value })}
              placeholder={k.sensitive && k.db_satt ? '••••••••' : ''}
            />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
        <button className="btn-primary" onClick={lagre}><Save size={13} /> Lagre</button>
        {mld && <span className="fm-text-muted" style={{ fontSize: 11 }}>{mld}</span>}
      </div>
      <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--theme-border-light, #2222)' }}>
        <strong style={{ fontSize: 12 }}>Test Twilio</strong>
        <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <input style={{ flex: '1 1 200px' }} value={testTil}
                 onChange={(e) => setTestTil(e.target.value)}
                 placeholder="+4799999999" />
          <button className="btn-ghost" onClick={testTwilio} disabled={!testTil}>
            <Send size={13} /> Send testmelding
          </button>
          {testMld && <span className="fm-text-muted" style={{ fontSize: 11 }}>{testMld}</span>}
        </div>
      </div>
      </>)}
    </Card>
  );
};

const BankForbindelserAdmin = ({ gcKonfigurert, ebKonfigurert }) => {
  const [rader, setRader] = useState(null);
  const [feil, setFeil] = useState('');
  const [busy, setBusy] = useState({});
  const [link, setLink] = useState(null); // { id, link }
  const [varselMld, setVarselMld] = useState('');

  const last = async () => {
    try {
      const r = await api.get('/admin/bank-forbindelser');
      setRader(r.data.forbindelser || []);
    } catch (e) {
      setFeil(e.response?.data?.error || 'Kunne ikke laste bank-forbindelser.');
    }
  };
  useEffect(() => { last(); }, []);

  const sendVarsel = async () => {
    setVarselMld('Sender…');
    try {
      const r = await api.post('/admin/cron/bank-utlop-varsel?tving=1', {});
      if (r.data.sendt > 0) {
        setVarselMld(`✓ Sendt til ${r.data.mottakere.join(', ')} (${r.data.utlopt} utløpt, ${r.data.kritisk} kritisk, ${r.data.snart} snart)`);
      } else {
        setVarselMld(r.data.grunn || 'Ingen varsel sendt.');
      }
    } catch (e) {
      setVarselMld('Feil: ' + (e.response?.data?.error || e.message));
    }
  };

  const fornye = async (bf) => {
    setBusy({ ...busy, [bf.id]: true });
    setLink(null);
    try {
      const r = await api.post(`/admin/bank-forbindelser/${bf.id}/fornye`, {});
      setLink({ id: bf.id, navn: bf.navn, firma: bf.firma_navn, link: r.data.link, requisition_id: r.data.requisition_id });
    } catch (e) {
      alert('Feil: ' + (e.response?.data?.error || e.message));
    }
    setBusy({ ...busy, [bf.id]: false });
  };

  const statusBadge = (s, dager) => {
    if (s === 'utlopt') return <span className="badge" style={{ background: '#ef4444', color: '#fff', fontSize: 9 }}>Utløpt</span>;
    if (s === 'kritisk') return <span className="badge" style={{ background: '#f59e0b', color: '#fff', fontSize: 9 }}>{dager}d</span>;
    if (s === 'snart') return <span className="badge" style={{ background: '#fbbf24', color: '#111', fontSize: 9 }}>{dager}d</span>;
    if (s === 'ok') return <span className="badge badge-success" style={{ fontSize: 9 }}>{dager}d</span>;
    return <span className="badge" style={{ fontSize: 9 }}>ukjent</span>;
  };

  return (
    <Card style={{ marginBottom: 12 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Wifi size={14} /> Bank-tilkoblinger (PSD2)
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <span className={`badge ${ebKonfigurert ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: 9 }}>
            Enable Banking: {ebKonfigurert ? 'aktivert' : 'mangler'}
          </span>
          <span className={`badge ${gcKonfigurert ? 'badge-success' : ''}`} style={{ fontSize: 9 }}>
            GoCardless: {gcKonfigurert ? 'aktivert' : 'av'}
          </span>
        </span>
      </h3>
      <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 12px' }}>
        PSD2-samtykke varer i 90 dager (regulatorisk krav). Du som admin ser her hvem som nærmer seg utløp,
        så du proaktivt kan generere ny samtykke-link og sende til kunden — uten at de selv må følge med.
        Sluttbrukerne får ingen feilmeldinger; sync stopper bare stille til ny samtykke er på plass.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <button className="btn-ghost" style={{ fontSize: 11 }} onClick={sendVarsel}>
          <Send size={11} /> Send varsel-epost nå
        </button>
        {varselMld && <span className="fm-text-muted" style={{ fontSize: 11 }}>{varselMld}</span>}
        <span className="fm-text-muted" style={{ fontSize: 10, marginLeft: 'auto' }}>
          Auto-varsel sendes daglig til superadmin-eposter ved første request etter midnatt.
        </span>
      </div>
      {feil && <p style={{ color: 'var(--theme-danger,#ef4444)', fontSize: 12, margin: 0 }}>{feil}</p>}
      {!feil && rader === null && <p className="fm-text-muted" style={{ fontSize: 12, margin: 0 }}>Laster…</p>}
      {!feil && rader && rader.length === 0 && (
        <p className="fm-text-muted" style={{ fontSize: 12, margin: 0 }}>
          Ingen PSD2-tilkoblinger registrert ennå.
        </p>
      )}
      {!feil && rader && rader.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Firma</th>
                <th style={{ textAlign: 'left' }}>Konto</th>
                <th style={{ textAlign: 'left' }}>IBAN</th>
                <th style={{ textAlign: 'left' }}>Sist sync</th>
                <th style={{ textAlign: 'right' }}>Saldo</th>
                <th style={{ textAlign: 'center' }}>Samtykke</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rader.map(r => (
                <tr key={r.id}>
                  <td>{r.firma_navn}</td>
                  <td>{r.navn}{!r.aktiv && <span className="badge badge-warning" style={{ fontSize: 9, marginLeft: 4 }}>inaktiv</span>}</td>
                  <td className="fm-text-muted">{r.iban || r.kontonummer || '—'}</td>
                  <td className="fm-text-muted">{r.sist_sync ? r.sist_sync.slice(0, 16).replace('T', ' ') : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{r.sist_saldo ? r.sist_saldo.toLocaleString('nb-NO', { minimumFractionDigits: 2 }) : '—'}</td>
                  <td style={{ textAlign: 'center' }}>{statusBadge(r.utlop_status, r.dager_til_utlop)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn-ghost" style={{ fontSize: 11 }} disabled={busy[r.id]}
                            onClick={() => fornye(r)}>
                      <RefreshCw size={11} /> Forny
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {link && (
        <div style={{ marginTop: 12, padding: 10, background: 'var(--theme-bg-subtle, #f5f5f5)', borderRadius: 6 }}>
          <strong style={{ fontSize: 12 }}>Ny samtykke-link for {link.navn} ({link.firma})</strong>
          <p className="fm-text-muted" style={{ fontSize: 11, margin: '4px 0' }}>
            Send denne lenken til kunden, eller åpne selv hvis du har banktilgang. Lenken må åpnes i samme nettleser
            som loggen på regnskap-appen for at samtykket skal mappes tilbake automatisk.
          </p>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input readOnly value={link.link} style={{ flex: '1 1 300px', fontSize: 11 }} onFocus={(e) => e.target.select()} />
            <button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => { navigator.clipboard.writeText(link.link); }}>
              <Copy size={11} /> Kopier
            </button>
            <a className="btn-ghost" style={{ fontSize: 11 }} href={link.link} target="_blank" rel="noreferrer">
              <ExternalLink size={11} /> Åpne
            </a>
          </div>
        </div>
      )}
    </Card>
  );
};

const TjenesterAdmin = () => {
  const [rader, setRader] = useState(null);
  const [feil, setFeil] = useState('');

  const last = () => api.get('/admin/tjenester')
    .then((r) => setRader(r.data.foresporsler || []))
    .catch((e) => setFeil(e.response?.data?.error || 'Kunne ikke laste.'));
  useEffect(() => { last(); }, []);

  const settStatus = async (id, status) => {
    try { await api.post(`/admin/tjenester/${id}/status`, { status }); last(); }
    catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const badge = (s) => {
    if (s === 'kontaktet') return <span className="badge" style={{ background: '#fbbf24', color: '#111', fontSize: 9 }}>kontaktet</span>;
    if (s === 'lukket') return <span className="badge badge-success" style={{ fontSize: 9 }}>lukket</span>;
    return <span className="badge badge-warning" style={{ fontSize: 9 }}>ny</span>;
  };

  const antallNye = (rader || []).filter((r) => r.status === 'ny').length;

  return (
    <Card style={{ marginBottom: 12 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Sparkles size={14} /> Tjenesteforespørsler
        {antallNye > 0 && <span className="badge badge-warning" style={{ fontSize: 9 }}>{antallNye} nye</span>}
      </h3>
      <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 12px' }}>
        Forespørsler om regnskapsfører, revisor, årsoppgjør m.m. sendt fra Tjenester-siden.
        Superadmin får e-postvarsel ved nye forespørsler.
      </p>
      {feil && <p style={{ color: 'var(--theme-danger,#ef4444)', fontSize: 12, margin: 0 }}>{feil}</p>}
      {!feil && rader === null && <p className="fm-text-muted" style={{ fontSize: 12, margin: 0 }}>Laster…</p>}
      {!feil && rader && rader.length === 0 && (
        <p className="fm-text-muted" style={{ fontSize: 12, margin: 0 }}>Ingen forespørsler ennå.</p>
      )}
      {!feil && rader && rader.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Tjeneste</th>
                <th style={{ textAlign: 'left' }}>Firma</th>
                <th style={{ textAlign: 'left' }}>Kontakt</th>
                <th style={{ textAlign: 'left' }}>Melding</th>
                <th style={{ textAlign: 'left' }}>Sendt</th>
                <th style={{ textAlign: 'left' }}>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rader.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.tjeneste_navn}</b></td>
                  <td>{r.firma_navn}</td>
                  <td style={{ fontSize: 11 }}>
                    {r.kontakt_navn && <div>{r.kontakt_navn}</div>}
                    <a href={`mailto:${r.kontakt_epost}`}>{r.kontakt_epost}</a>
                    {r.kontakt_tlf && <div className="fm-text-muted">{r.kontakt_tlf}</div>}
                  </td>
                  <td style={{ fontSize: 11, maxWidth: 220 }}>{r.melding || '—'}</td>
                  <td className="fm-text-muted" style={{ fontSize: 11 }}>{(r.opprettet || '').slice(0, 10)}</td>
                  <td>{badge(r.status)}</td>
                  <td>
                    <select value={r.status} onChange={(e) => settStatus(r.id, e.target.value)}
                            style={{ fontSize: 11, padding: '2px 4px' }}>
                      <option value="ny">ny</option>
                      <option value="kontaktet">kontaktet</option>
                      <option value="lukket">lukket</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

// ── Organisasjoner & abonnement ──────────────────────────────────────────────
const PLAN_STATUS_VALG = ['free', 'active', 'past_due', 'canceled'];
const BETALING_VALG = ['manuell', 'stripe', 'vipps', 'ehf'];

const OrganisasjonerAdmin = () => {
  const [orgs, setOrgs] = useState(null);
  const [plans, setPlans] = useState([]);
  const [feil, setFeil] = useState('');
  const [mld, setMld] = useState('');
  const [lagrer, setLagrer] = useState(null);

  const last = () => api.get('/sa/organizations')
    .then((r) => { setOrgs(r.data.organizations || []); setPlans(r.data.plans || []); })
    .catch((e) => setFeil(e.response?.data?.error || 'Kunne ikke laste organisasjoner.'));
  useEffect(() => { last(); }, []);

  const endre = (id, felt, verdi) =>
    setOrgs((os) => os.map((o) => (o.id === id ? { ...o, [felt]: verdi } : o)));

  const lagre = async (o) => {
    setLagrer(o.id); setMld('');
    try {
      await api.post(`/sa/organizations/${o.id}/plan`, {
        plan: o.plan,
        status: o.planStatus || '',
        renewsAt: (o.planRenewsAt || '').slice(0, 10),
      });
      await api.post(`/sa/organizations/${o.id}/betaling`, {
        betalingsmetode: o.betalingsmetode || 'manuell',
      });
      setMld(`Lagret for ${o.navn || ('firma #' + o.id)}.`);
      await last();
    } catch (e) {
      setMld(e.response?.data?.error || 'Lagring feilet.');
    }
    setLagrer(null);
  };

  const planNavn = (id) => (plans.find((p) => p.id === id)?.navn) || id;

  return (
    <Card style={{ marginBottom: 12 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Building2 size={14} /> Organisasjoner & abonnement
        {orgs && <span className="badge" style={{ fontSize: 9 }}>{orgs.length}</span>}
      </h3>
      <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 12px' }}>
        Endre plan, status, fornyelsesdato og betalingsmetode per firma. Endringer trer i kraft umiddelbart.
      </p>
      {feil && <p style={{ color: 'var(--theme-danger,#ef4444)', fontSize: 12, margin: 0 }}>{feil}</p>}
      {mld && <p style={{ color: 'var(--theme-success,#16a34a)', fontSize: 12, margin: '0 0 8px' }}>{mld}</p>}
      {!feil && orgs === null && <p className="fm-text-muted" style={{ fontSize: 12, margin: 0 }}>Laster…</p>}
      {!feil && orgs && orgs.length === 0 && (
        <p className="fm-text-muted" style={{ fontSize: 12, margin: 0 }}>Ingen firmaer registrert.</p>
      )}
      {!feil && orgs && orgs.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Firma</th>
                <th style={{ textAlign: 'left' }}>Brukere</th>
                <th style={{ textAlign: 'left' }}>Plan</th>
                <th style={{ textAlign: 'left' }}>Status</th>
                <th style={{ textAlign: 'left' }}>Fornyes</th>
                <th style={{ textAlign: 'left' }}>Betaling</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.id}>
                  <td>
                    <b>{o.navn || `firma #${o.id}`}</b>
                    {o.orgnr && <div className="fm-text-muted" style={{ fontSize: 10 }}>{o.orgnr}</div>}
                    {o.erForening && <span className="badge" style={{ fontSize: 9 }}>forening</span>}
                  </td>
                  <td>{o.antallBrukere ?? '—'}</td>
                  <td>
                    <select value={o.plan || 'free'} onChange={(e) => endre(o.id, 'plan', e.target.value)}
                            style={{ fontSize: 11, padding: '2px 4px' }}>
                      {plans.map((p) => (
                        <option key={p.id} value={p.id}>{p.navn} ({p.pris_mnd} kr)</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select value={o.planStatus || 'free'} onChange={(e) => endre(o.id, 'planStatus', e.target.value)}
                            style={{ fontSize: 11, padding: '2px 4px' }}>
                      {PLAN_STATUS_VALG.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>
                    <input type="date" value={(o.planRenewsAt || '').slice(0, 10)}
                           onChange={(e) => endre(o.id, 'planRenewsAt', e.target.value)}
                           style={{ fontSize: 11, padding: '2px 4px' }} />
                  </td>
                  <td>
                    <select value={o.betalingsmetode || 'manuell'} onChange={(e) => endre(o.id, 'betalingsmetode', e.target.value)}
                            style={{ fontSize: 11, padding: '2px 4px' }}>
                      {BETALING_VALG.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </td>
                  <td>
                    <button className="btn btn-sm" disabled={lagrer === o.id}
                            onClick={() => lagre(o)}
                            style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Save size={12} /> {lagrer === o.id ? 'Lagrer…' : 'Lagre'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

// ── Brukere (rediger / slett) ────────────────────────────────────────────────
const ROLLE_VALG = ['admin', 'regnskap', 'revisor', 'ansatt'];

const BrukereAdmin = () => {
  const [brukere, setBrukere] = useState(null);
  const [sok, setSok] = useState('');
  const [feil, setFeil] = useState('');
  const [mld, setMld] = useState('');

  const last = (q) => api.get('/sa/users', { params: q ? { q } : {} })
    .then((r) => setBrukere(r.data.users || []))
    .catch((e) => setFeil(e.response?.data?.error || 'Kunne ikke laste brukere.'));
  useEffect(() => { last(); }, []);

  const endre = (id, felt, verdi) =>
    setBrukere((us) => us.map((u) => (u.id === id ? { ...u, [felt]: verdi } : u)));

  const lagre = async (u) => {
    setMld('');
    try {
      await api.patch(`/sa/users/${u.id}`, { navn: u.navn, epost: u.epost, rolle: u.rolle });
      setMld(`Lagret ${u.epost}.`);
      await last(sok);
    } catch (e) { setMld(e.response?.data?.error || 'Lagring feilet.'); }
  };

  const nyttPassord = async (u) => {
    const p = window.prompt(`Nytt passord for ${u.epost}:`);
    if (!p) return;
    setMld('');
    try {
      await api.patch(`/sa/users/${u.id}`, { nyttPassord: p });
      setMld(`Passord oppdatert for ${u.epost}.`);
    } catch (e) { setMld(e.response?.data?.error || 'Feilet.'); }
  };

  const slett = async (u) => {
    if (!window.confirm(`Slette bruker ${u.epost}? Dette kan ikke angres.`)) return;
    setMld('');
    try {
      await api.delete(`/sa/users/${u.id}`);
      setMld(`Slettet ${u.epost}.`);
      await last(sok);
    } catch (e) { setMld(e.response?.data?.error || 'Sletting feilet.'); }
  };

  return (
    <Card style={{ marginBottom: 12 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Users size={14} /> Brukere
        {brukere && <span className="badge" style={{ fontSize: 9 }}>{brukere.length}</span>}
      </h3>
      <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 12px' }}>
        Rediger navn, e-post og rolle, tilbakestill passord eller slett brukere på tvers av alle firmaer.
      </p>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <input value={sok} onChange={(e) => setSok(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') last(sok); }}
               placeholder="Søk navn eller e-post…"
               style={{ fontSize: 12, padding: '4px 8px', flex: 1, maxWidth: 280 }} />
        <button className="btn btn-sm" onClick={() => last(sok)} style={{ fontSize: 11 }}>Søk</button>
      </div>
      {feil && <p style={{ color: 'var(--theme-danger,#ef4444)', fontSize: 12, margin: 0 }}>{feil}</p>}
      {mld && <p style={{ color: 'var(--theme-success,#16a34a)', fontSize: 12, margin: '0 0 8px' }}>{mld}</p>}
      {!feil && brukere === null && <p className="fm-text-muted" style={{ fontSize: 12, margin: 0 }}>Laster…</p>}
      {!feil && brukere && brukere.length === 0 && (
        <p className="fm-text-muted" style={{ fontSize: 12, margin: 0 }}>Ingen brukere funnet.</p>
      )}
      {!feil && brukere && brukere.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Navn</th>
                <th style={{ textAlign: 'left' }}>E-post</th>
                <th style={{ textAlign: 'left' }}>Rolle</th>
                <th style={{ textAlign: 'left' }}>Firma</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {brukere.map((u) => (
                <tr key={u.id}>
                  <td>
                    <input value={u.navn || ''} onChange={(e) => endre(u.id, 'navn', e.target.value)}
                           style={{ fontSize: 11, padding: '2px 4px', width: 130 }} />
                  </td>
                  <td>
                    <input value={u.epost || ''} onChange={(e) => endre(u.id, 'epost', e.target.value)}
                           style={{ fontSize: 11, padding: '2px 4px', width: 190 }} />
                  </td>
                  <td>
                    <select value={u.rolle || 'admin'} onChange={(e) => endre(u.id, 'rolle', e.target.value)}
                            style={{ fontSize: 11, padding: '2px 4px' }}>
                      {ROLLE_VALG.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td style={{ fontSize: 11 }}>
                    {u.firmaNavn || `#${u.firmaId}`}
                    {u.totp && <span className="badge badge-success" style={{ fontSize: 8, marginLeft: 4 }}>2FA</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-sm" onClick={() => lagre(u)}
                            style={{ fontSize: 11, marginRight: 4, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <Save size={12} /> Lagre
                    </button>
                    <button className="btn btn-sm" onClick={() => nyttPassord(u)}
                            style={{ fontSize: 11, marginRight: 4, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <KeyRound size={12} /> Passord
                    </button>
                    <button className="btn btn-sm" onClick={() => slett(u)}
                            style={{ fontSize: 11, color: 'var(--theme-danger,#ef4444)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <Trash2 size={12} /> Slett
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default Admin;
